/**
 * "Din plan från förra övningstentan" (pedagogy spec §7.3.8): the plan line written
 * after the most recently graded exam. The lobby shows it at the top of the
 * start card, so it is read before the clock starts; the exam page shows it
 * above the instruction line with "Dölj". Advisory only — it never blocks the
 * timer.
 */
import clsx from "clsx";
import { useId } from "react";
import { Button } from "@client/components/ui";
import { useLang, useT } from "@client/i18n";
import { formatDate } from "@client/lib/format";
import type { LastPlanDto } from "../types";

export interface LastPlanNoteProps {
  plan: LastPlanDto;
  /** 3 inside the lobby's start card, 2 on the exam page. */
  headingLevel?: 2 | 3;
  /** Shows "Dölj" when given. */
  onHide?: () => void;
  className?: string;
}

export function LastPlanNote({ plan, headingLevel = 3, onHide, className }: LastPlanNoteProps) {
  const t = useT();
  const { lang } = useLang();
  const headingId = useId();
  const Heading = headingLevel === 2 ? "h2" : "h3";

  return (
    <div
      role="note"
      aria-labelledby={headingId}
      className={clsx(
        "rounded-xl border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-900 dark:border-accent-700 dark:bg-accent-950 dark:text-accent-100",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <Heading id={headingId} className="font-medium">
          {t("exam.lastPlan.title")}
        </Heading>
        {onHide && (
          <Button
            variant="ghost"
            size="sm"
            className="max-sm:min-h-11 max-sm:min-w-11"
            onClick={onHide}
          >
            {t("exam.lastPlan.hide")}
          </Button>
        )}
      </div>
      <p className="mt-1 whitespace-pre-line">{plan.text}</p>
      <p className="mt-1 text-xs">
        {t("exam.lastPlan.written", { date: formatDate(plan.savedAt, lang) })}
      </p>
    </div>
  );
}
